import { useEffect, useRef, useMemo, useState } from 'react';
import { useCurrentUser } from '../api/endpoints/useCurrentUser';
import { useClientId } from './useClientId';
import { useI18n, useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { DateTimeUtils } from '@locus-taxy/encore-utils';
import { getApiBaseUrl } from '../config';

/**
 * Upper bound on how long the app waits for the active-locale catalog before
 * rendering anyway. Guards against a slow/failed catalog fetch hanging the app —
 * past this the UI paints with inline English defaults.
 */
const CATALOG_READY_TIMEOUT_MS = 3000;

const useConfigureLocale = () => {
  const clientId = useClientId();

  // Get current user data
  const { data: currentUserResponse, isFetched: isCurrentUserFetched } =
    useCurrentUser(clientId);

  // Shared i18next instance; useTranslation re-renders this hook's host on
  // 'loaded'/'languageChanged' so the readiness check below re-evaluates.
  const { i18n } = useTranslation();

  const apiBaseUrl = useMemo(() => {
    if (!clientId) {
      return '';
    }
    return getApiBaseUrl(clientId);
  }, [clientId]);

  // v8: the language switch happens in place on the ONE shared i18next
  // instance via changeLanguage() — the backend reloads the target language's
  // catalog. There is no per-language instance creation and no provider
  // remount (v7's init18nWithUserLanguage instance-swap is gone).
  const { changeLanguage } = useI18n({
    clientId,
    apiBaseUrl,
  });

  const lastAppliedRef = useRef<{
    locale: string | null;
    changeLanguageFn: typeof changeLanguage | null;
  }>({ locale: null, changeLanguageFn: null });

  const localeIdentifier =
    currentUserResponse?.data?.entityData?.preferences?.localeIdentifier ??
    null;

  const timezone =
    currentUserResponse?.data?.entityData?.preferences?.timezone ?? null;

  useEffect(() => {
    if (!localeIdentifier) {
      return;
    }

    const changeLanguageFnChanged =
      lastAppliedRef.current?.changeLanguageFn !== changeLanguage;
    const localeChanged = lastAppliedRef.current?.locale !== localeIdentifier;

    if (!changeLanguageFnChanged && !localeChanged) {
      return;
    }

    void changeLanguage(localeIdentifier);

    lastAppliedRef.current = {
      locale: localeIdentifier,
      changeLanguageFn: changeLanguage,
    };
  }, [changeLanguage, localeIdentifier]);

  useEffect(() => {
    if (!timezone || !localeIdentifier) {
      return;
    }

    const dateTimeUtilsInstance = DateTimeUtils.getInstance();
    dateTimeUtilsInstance.setTimezone(timezone);
    dateTimeUtilsInstance.setLocale(localeIdentifier);
  }, [timezone, localeIdentifier]);

  // Gate first paint until the active locale's catalog is loaded. The catalog
  // is fetched async after mount, so without this the shell (disco-ui shim,
  // memoized form schemas) renders inline English defaults and — being built
  // once — never re-resolves. Falling back on a timeout keeps a slow/failed
  // fetch from hanging the app.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), CATALOG_READY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, []);

  const targetLocale = localeIdentifier ?? 'en-US';
  const catalogLoaded = i18n.hasResourceBundle(targetLocale, 'common');
  const localeReady = timedOut || (isCurrentUserFetched && catalogLoaded);

  return { localeReady };
};

export default useConfigureLocale;
